import styled, {useTheme} from 'styled-components';
import {Icon, Text} from '@forecast-it/design-system';
import type {TicketPriority} from '../types/ticket';
import {PRIORITY_LABELS} from '../constants/priority';

// Priority reads as a flag glyph plus its label — never a filled pill. A queue row
// already carries a status pill and an SLA colour, so a third colour block would
// cost more than it tells the reader.

/**
 * Semantic icon colours (`sys.color.iconfill` keys). Medium is deliberately absent:
 * jeopardy has three steps (error / warning / greyed-out) and Medium sits between
 * warning and Low, where no semantic token fits — see {@link mediumFlagColor}.
 */
const FLAG_COLOR: Record<Exclude<TicketPriority, 'p2'>, 'error' | 'warning' | 'disabled'> = {
	p0: 'error',
	p1: 'warning',
	p3: 'disabled',
};

// Just the slice of the semantic token tree this badge reads, so it states its own
// requirements rather than depending on the design system's full theme type (which
// is unavailable when the package resolves to a stub).
interface FlagColorTokens {
	sys?: {color?: {id?: Record<string, string>}};
}

/**
 * Medium takes the categorical Sky-blue tint (`sys.color.id.7`), not the brand's
 * electric blue: electric blue means "interactive" everywhere else in the product,
 * and a priority flag is not a control.
 */
function mediumFlagColor(theme: FlagColorTokens): string | undefined {
	return theme?.sys?.color?.id?.['7'];
}

const Row = styled.span`
	display: inline-flex;
	align-items: center;
	gap: ${p => p.theme.sys.spacing[100]};
	white-space: nowrap;
`;

interface Props {
	priority: TicketPriority;
}

/**
 * The flag glyph on its own, for hosts that supply their own label — the queue's
 * Priority column passes this as the cell icon and lets the table render the text.
 */
export function PriorityFlag({priority}: Props) {
	const theme = useTheme() as FlagColorTokens;
	return priority === 'p2' ? (
		<Icon icon="flag" size="s" customColor={mediumFlagColor(theme)} />
	) : (
		<Icon icon="flag" size="s" newColor={FLAG_COLOR[priority]} />
	);
}

export function PriorityBadge({priority}: Props) {
	const label = PRIORITY_LABELS[priority];
	return (
		<Row data-testid="priority-badge" data-priority={priority} aria-label={`Priority: ${label}`}>
			<PriorityFlag priority={priority} />
			<Text size="2">{label}</Text>
		</Row>
	);
}
